/**
 * Lógica pura de la pantalla de borrado de torneos del superadmin.
 *
 * El backend (#166) exige escribir el nombre EXACTO del torneo para confirmar
 * (409 tournament_delete_name_mismatch si no coincide) y compara sin espacios
 * sobrantes. Esta lib refleja esa regla para que el botón rojo solo se
 * habilite cuando el backend va a aceptar, y convierte los conteos crudos del
 * preview en renglones legibles en español.
 */

export interface DeletionCounts {
  participants: number;
  matches: number;
  registration_orders: number;
  paid_orders: number;
  ranking_point_events: number;
  ranking_links: number;
  checkout_sessions: number;
  draw_ceremonies: number;
}

/** Igual que el backend: coincidencia exacta ignorando espacios en los bordes. */
export function confirmNameMatches(expected: string, typed: string): boolean {
  const objetivo = (expected ?? "").trim();
  if (!objetivo) return false;
  return typed.trim() === objetivo;
}

const COUNT_LABELS: Array<{
  key: keyof DeletionCounts;
  singular: string;
  plural: string;
}> = [
  { key: "participants", singular: "participante", plural: "participantes" },
  { key: "matches", singular: "partido", plural: "partidos" },
  {
    key: "registration_orders",
    singular: "orden de inscripción",
    plural: "órdenes de inscripción",
  },
  {
    key: "checkout_sessions",
    singular: "sesión de cobro",
    plural: "sesiones de cobro",
  },
  {
    key: "ranking_point_events",
    singular: "evento de puntos de ranking",
    plural: "eventos de puntos de ranking",
  },
  {
    key: "ranking_links",
    singular: "liga de ranking",
    plural: "ligas de ranking",
  },
  { key: "draw_ceremonies", singular: "acta de sorteo", plural: "actas de sorteo" },
];

/**
 * Renglones "N cosa(s)" solo para los conteos > 0, en orden fijo.
 *
 * paid_orders NO sale aquí: es un subconjunto de registration_orders y ya
 * viene destacado en los warnings del backend; repetirlo sumaría de más.
 */
export function deletionCountLines(counts: DeletionCounts): string[] {
  const lines: string[] = [];
  for (const { key, singular, plural } of COUNT_LABELS) {
    const value = counts[key] ?? 0;
    if (value <= 0) continue;
    lines.push(`${value} ${value === 1 ? singular : plural}`);
  }
  return lines;
}

/** Un torneo sin nada dentro sigue pidiendo confirmación, pero se dice claro. */
export function deletionIsEmpty(counts: DeletionCounts): boolean {
  return deletionCountLines(counts).length === 0;
}
